import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Search, Loader2, Check } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { COUNTRIES, suggestedCurrency } from "../../lib/countries";
import { CURRENCIES } from "../../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 100;

const CountrySelect = ({ value, onChange, testid }) => (
  <select value={COUNTRIES.includes(value) ? value : "Australia"} onChange={(e) => onChange(e.target.value)}
    className="h-8 w-full rounded-md border border-[#DAD5CA] bg-white px-1.5 text-[12.5px] text-[#1A1A1A]"
    data-testid={testid}>
    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
  </select>
);

const CurrencySelect = ({ value, onChange, testid }) => (
  <select value={(value || "AUD").toUpperCase()} onChange={(e) => onChange(e.target.value)}
    className="h-8 w-full rounded-md border border-[#DAD5CA] bg-white px-1.5 text-[12.5px] mono text-[#1A1A1A]"
    data-testid={testid}>
    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
  </select>
);

/* Countries & Currencies tidy-up: set where a company trades from and the
   currency they get quoted in — inline, or across a whole selection. */
export default function AdminGeoTidyUp() {
  const [kind, setKind] = useState("client");
  const [q, setQ] = useState("");
  const [overseasOnly, setOverseasOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});          // key -> row
  const [bulkCountry, setBulkCountry] = useState("United Kingdom");
  const [bulkCurrency, setBulkCurrency] = useState("GBP");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lookups/admin/geo`, {
        params: { kind, q, overseas_only: overseasOnly, limit: PAGE, skip: page * PAGE },
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load companies"); }
    finally { setLoading(false); }
  }, [kind, q, overseasOnly, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel({}); setPage(0); }, [kind, overseasOnly]);

  const counts = useMemo(() => rows.reduce((m, r) => ({ ...m, [r.currency]: (m[r.currency] || 0) + 1 }), {}), [rows]);
  const selected = Object.values(sel);
  const rowKey = (r) => `${r.kind}:${r.id}`;

  const apply = async (items, country, currency) => {
    setBusy(true);
    try {
      const body = { items: items.map((r) => ({ kind: r.kind, id: r.id, name: r.name })) };
      if (country !== undefined) body.country = country;
      if (currency !== undefined) body.currency = currency;
      const r = await axios.post(`${API}/lookups/admin/geo/bulk`, body);
      toast.success(`Updated ${r.data.updated} ${r.data.updated === 1 ? "company" : "companies"}`);
      setSel({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };

  // Picking a country nudges the currency we'd normally quote them in.
  const setRowCountry = (row, country) => apply([row], country, suggestedCurrency(country));
  const setRowCurrency = (row, currency) => apply([row], undefined, currency);

  return (
    <div className="space-y-4" data-testid="admin-geo-tidyup">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#DDF0E6] flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5 text-[#0E6B3F]" />
        </div>
        <div>
          <h3 className="heading text-lg text-[#1A1A1A] tracking-tight">Countries &amp; Currencies</h3>
          <p className="text-sm text-[#59554D]">Set where each company trades from and the currency they get quoted in. Overseas companies are quoted tax-free — no GST is collected.</p>
        </div>
      </div>

      <Card className="bg-white border-[#DAD5CA]">
        <CardContent className="p-0">
          {/* Filters */}
          <div className="flex items-end gap-2 p-4 border-b border-[#EDEAE3] flex-wrap">
            <div className="flex items-center gap-1.5">
              {[["client", "Clients"], ["prospect", "Prospects"]].map(([k, label]) => (
                <button key={k} onClick={() => setKind(k)}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold transition-colors ${kind === k ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
                  data-testid={`geo-kind-${k}`}>{label}</button>
              ))}
            </div>
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A39D8F]" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="Search by company name…" className="h-9 pl-8 border-[#DAD5CA]" data-testid="geo-search" />
            </div>
            <button onClick={() => setOverseasOnly((v) => !v)}
              className={`h-8 px-3 rounded-full text-[12px] font-semibold transition-colors ${overseasOnly ? "bg-[#0E6B3F] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
              data-testid="geo-overseas-only">Overseas only</button>
            <div className="flex items-center gap-1.5 text-[11.5px] text-[#59554D]" data-testid="geo-counts">
              {CURRENCIES.map((c) => (
                <span key={c.code} className="mono px-1.5 py-0.5 rounded bg-[#F4F2ED] border border-[#EDEAE3]">
                  {c.code} {counts[c.code] || 0}
                </span>
              ))}
            </div>
          </div>

          {/* Bulk bar */}
          {selected.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#EDEAE3] bg-[#FFF8D6] flex-wrap" data-testid="geo-bulk-bar">
              <span className="text-[12.5px] font-semibold text-[#1A1A1A]">{selected.length} selected</span>
              <div className="w-48"><CountrySelect value={bulkCountry} onChange={(c) => { setBulkCountry(c); setBulkCurrency(suggestedCurrency(c)); }} testid="geo-bulk-country" /></div>
              <div className="w-24"><CurrencySelect value={bulkCurrency} onChange={setBulkCurrency} testid="geo-bulk-currency" /></div>
              <Button size="sm" disabled={busy} onClick={() => apply(selected, bulkCountry, bulkCurrency)}
                className="h-8 bg-[#1A1A1A] hover:bg-[#333] text-white" data-testid="geo-bulk-apply">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Apply to selected
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSel({})} className="h-8 text-[#59554D]" data-testid="geo-bulk-clear">Clear</Button>
            </div>
          )}

          {/* Header */}
          <div className="hidden sm:flex items-center gap-3 px-4 py-2 border-b border-[#EDEAE3] bg-[#FBFAF7] text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-semibold">
            <input type="checkbox" className="w-3.5 h-3.5" data-testid="geo-select-all"
              checked={rows.length > 0 && selected.length === rows.length}
              onChange={(e) => setSel(e.target.checked ? rows.reduce((m, r) => ({ ...m, [rowKey(r)]: r }), {}) : {})} />
            <span className="flex-1">Company</span>
            <span className="w-16">Type</span>
            <span className="w-48">Country</span>
            <span className="w-24">Currency</span>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-[#8A857A]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-[#8A857A]">
              {overseasOnly ? "No overseas companies yet — everyone is set to Australia / AUD." : "No companies match that search."}
            </div>
          ) : (
            <div>
              {rows.map((r) => (
                <div key={rowKey(r)} className="flex items-center gap-3 px-4 py-2 border-b border-[#F4F2ED] hover:bg-[#FBFAF7] flex-wrap sm:flex-nowrap"
                  data-testid={`geo-row-${r.id}`}>
                  <input type="checkbox" className="w-3.5 h-3.5" data-testid={`geo-check-${r.id}`}
                    checked={!!sel[rowKey(r)]}
                    onChange={(e) => setSel((s) => {
                      const n = { ...s };
                      if (e.target.checked) n[rowKey(r)] = r; else delete n[rowKey(r)];
                      return n;
                    })} />
                  <span className="flex-1 min-w-[140px] text-[13px] text-[#1A1A1A] truncate" title={r.name}>{r.name}</span>
                  <span className="w-16 text-[10px] uppercase tracking-wide text-[#8A857A]">{r.kind}</span>
                  <div className="w-48"><CountrySelect value={r.country} onChange={(c) => setRowCountry(r, c)} testid={`geo-country-${r.id}`} /></div>
                  <div className="w-24"><CurrencySelect value={r.currency} onChange={(c) => setRowCurrency(r, c)} testid={`geo-currency-${r.id}`} /></div>
                </div>
              ))}
            </div>
          )}

          {kind === "prospect" && total > PAGE && (
            <div className="flex items-center justify-between px-4 py-2.5 text-[12px] text-[#59554D]">
              <span>Showing {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}</span>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8 border-[#DAD5CA]" data-testid="geo-prev">Previous</Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)} className="h-8 border-[#DAD5CA]" data-testid="geo-next">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
